function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/* @pareto-engineering/generator-front 1.1.1-alpha.2 */
import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import styleNames from '@pareto-engineering/bem/exports';
import { Button } from "../../../../b";
import "./styles.scss";

// Local Definitions

import { Section } from "./common";
const baseClassName = styleNames.base;
const componentClassName = 'renderer';
const validate = (currentSectionIndex, formData, values) => {
  const errors = {};
  const hasAtLeastOneValue = Object.keys(values).some(valueKey => {
    const inputValue = values[valueKey];
    return inputValue && inputValue !== '';
  });
  if (!hasAtLeastOneValue) {
    errors.message = 'At least one input must have a value';
  }

  // Check required fields for current section
  formData.sections[currentSectionIndex].inputs.forEach(input => {
    if (input.required && !values[input.name]) {
      errors[input.name] = 'This field is required';
    }
  });
  return errors;
};
const reconstructFormDataWithValues = (formData, values) => {
  const valuesMap = {};
  Object.keys(values).forEach(async key => {
    if (!key.includes('files')) {
      valuesMap[key] = values[key];
    }
  });
  const newData = {
    ...formData,
    sections: formData.sections.map(section => ({
      ...section,
      inputs: section.inputs.map(input => ({
        ...input,
        value: valuesMap[input.name] !== undefined ? valuesMap[input.name] : input.value
      }))
    }))
  };
  return newData;
};

/**
 * This is the component description.
 */
const Renderer = ({
  id,
  className: userClassName,
  style,
  formData,
  readOnly,
  onSave,
  shouldSubmit,
  fileUploadStatus,
  handleFileDelete,
  onFileUpload,
  shouldUpdateInputStateInRealTime = true
  // ...otherProps
}) => {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [sectionHistory, setSectionHistory] = useState([]);
  const [updatedFormData, setUpdatedFormData] = useState({
    ...formData
  });
  useEffect(() => {
    setUpdatedFormData(formData);
  }, [formData]);
  const initialValues = formData.sections.reduce((acc, section) => {
    section.inputs.forEach(input => {
      acc[input.name] = input.value || '';
    });
    return acc;
  }, {});
  const handleSubmit = values => {
    const currentSection = updatedFormData.sections[currentSectionIndex];
    // by default, 'next' section is the immediate section after the current one
    let nextSectionIndex = currentSectionIndex + 1;

    // Any other value is either submit or an index to a section
    if (currentSection.navigation.nextSection !== 'next') {
      nextSectionIndex = currentSection.navigation.nextSection === 'submit' ? updatedFormData.sections.length // submit
      : parseInt(currentSection.navigation.nextSection, 10); // go to specific section
    }
    if (nextSectionIndex >= updatedFormData.sections.length) {
      // submit
      onSave(values);
    } else {
      // Record index history to navigate back
      setSectionHistory([...sectionHistory, currentSectionIndex]);
      // go to evaluated next section
      setCurrentSectionIndex(nextSectionIndex);
    }
  };
  const handlePrevious = () => {
    if (sectionHistory.length > 0) {
      // get the last index of array(the immediate last section)
      const previousSectionIndex = sectionHistory.pop();
      // set the immediate last section indx as current
      setCurrentSectionIndex(previousSectionIndex);
      setSectionHistory([...sectionHistory]);
    }
  };
  const isSubmit = currentSectionIndex === updatedFormData.sections.length - 1 || updatedFormData.sections[currentSectionIndex].navigation.nextSection === 'submit';
  const ref = useRef(null);
  const currentSectionInputs = updatedFormData.sections[currentSectionIndex].inputs;
  const hasErrorsOnInitialRender = currentSectionInputs.some(input => input.required && !ref.current?.values[input.name] || ref.current?.errors[input.name]);
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    className: [baseClassName, componentClassName, userClassName].filter(e => e).join(' '),
    style: style
  }, /*#__PURE__*/React.createElement(Formik, {
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validate: values => validate(currentSectionIndex, updatedFormData, values),
    innerRef: ref
  }, ({
    values,
    errors
  }) => {
    useEffect(() => {
      if (shouldUpdateInputStateInRealTime) {
        const formDataWithValues = reconstructFormDataWithValues(updatedFormData, values);
        onSave?.(formDataWithValues);
      }
    }, [values]);
    const hasErrors = Object.keys(errors).length > 0;
    return /*#__PURE__*/React.createElement(Form, null, updatedFormData.sections.map((section, sectionIndex) => sectionIndex === currentSectionIndex && /*#__PURE__*/React.createElement(Section, _extends({
      key: `${section.title}`
    }, section, {
      readOnly: readOnly,
      fileUploadStatus: fileUploadStatus,
      handleFileDelete: handleFileDelete,
      onFileUpload: onFileUpload,
      sectionIndex: sectionIndex
    }))), /*#__PURE__*/React.createElement("div", {
      className: "navigator-container"
    }, updatedFormData.sections.length > 1 && /*#__PURE__*/React.createElement(Button, {
      className: "previous-button",
      color: "interactive",
      isGradient: true,
      isCompact: true,
      isGhost: true,
      onClick: handlePrevious,
      disabled: sectionHistory.length === 0
    }, "Previous"), (!isSubmit || shouldSubmit) && /*#__PURE__*/React.createElement(Button, {
      color: "interactive",
      isGradient: true,
      type: "submit",
      disabled: hasErrors || hasErrorsOnInitialRender
    }, isSubmit ? 'Submit' : 'Next')));
  }));
};
Renderer.propTypes = {
  /**
   * The HTML id for this element
   */
  id: PropTypes.string,
  /**
   * The HTML class names for this element
   */
  className: PropTypes.string,
  /**
   * The React-written, css properties for this element.
   */
  style: PropTypes.objectOf(PropTypes.string)
};
Renderer.defaultProps = {
  // someProp: false,
};
export default Renderer;